/**
 * The formal Section -> Division -> code tree.
 *
 * The secondary browse, not the primary one. The A-U sections are the official
 * statistical taxonomy, in which recruitment lives under "Administrative and
 * support service activities" and a software house under "Information and
 * communication" - accurate, and not how anyone thinks about their market. The
 * curated category cards are the front door; this is here for the user who
 * knows the classification and wants to walk it.
 *
 * Divisions load their codes on expand rather than up front: 731 codes across
 * 88 divisions is a lot of DOM for something most users open twice.
 */

import { useCallback, useEffect, useState } from 'react';
import { sicApi } from '../lib/api.js';
import { IconChevronDown, IconChevronRight } from './Icons.jsx';
import SicCodeRow from './SicCodeRow.jsx';

export default function ClassificationTree({ selected, onToggleSelect, onAcquire }) {
  const [sections, setSections] = useState(null);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState(() => new Set());
  const [openDivisions, setOpenDivisions] = useState(() => new Set());
  const [divisionCodes, setDivisionCodes] = useState({});
  // Acquisition badges for every division opened so far, merged into one map -
  // browse returns state only for the page it served, and a division closed and
  // reopened should not lose its badges.
  const [states, setStates] = useState({});

  useEffect(() => {
    sicApi
      .sections()
      .then((data) => setSections(data.sections ?? []))
      .catch((caught) => setError(caught.message));
  }, []);

  const toggleSection = (section) =>
    setOpenSections((current) => {
      const next = new Set(current);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  const toggleDivision = useCallback(
    (division) => {
      setOpenDivisions((current) => {
        const next = new Set(current);
        if (next.has(division)) {
          next.delete(division);
          return next;
        }
        next.add(division);
        return next;
      });

      // Fetched once and kept - a division's codes do not change while the page
      // is open.
      setDivisionCodes((current) => {
        if (current[division]) return current;
        sicApi
          .browse({ division, limit: 200 })
          .then((data) => {
            setDivisionCodes((latest) => ({ ...latest, [division]: data.items ?? [] }));
            setStates((latest) => ({ ...latest, ...(data.acquisitionStates ?? {}) }));
          })
          .catch((caught) => setError(caught.message));
        return current;
      });
    },
    [],
  );

  if (error) {
    return (
      <div className="alert alert-error">
        <span className="alert-body">{error}</span>
      </div>
    );
  }

  if (!sections) {
    return (
      <div className="loading-panel">
        <span className="spinner" />
        Loading the classification
      </div>
    );
  }

  return (
    <div className="tree">
      {sections.map((section) => {
        const isOpen = openSections.has(section.section);
        return (
          <div className="tree-node" key={section.section}>
            <button
              type="button"
              className="tree-toggle"
              onClick={() => toggleSection(section.section)}
              aria-expanded={isOpen}
            >
              <span className="tree-toggle-chevron">
                {isOpen ? <IconChevronDown /> : <IconChevronRight />}
              </span>
              <span className="tree-toggle-key">Section {section.section}</span>
              <span className="tree-toggle-label">{section.sectionName}</span>
              <span className="pill-count">{section.codeCount}</span>
            </button>

            {isOpen ? (
              <div className="tree-children">
                {section.divisions.map((division) => {
                  const divisionOpen = openDivisions.has(division.division);
                  const codes = divisionCodes[division.division];
                  return (
                    <div key={division.division}>
                      <button
                        type="button"
                        className="tree-toggle"
                        onClick={() => toggleDivision(division.division)}
                        aria-expanded={divisionOpen}
                      >
                        <span className="tree-toggle-chevron">
                          {divisionOpen ? <IconChevronDown /> : <IconChevronRight />}
                        </span>
                        <span className="tree-toggle-key">{division.division}</span>
                        <span className="tree-toggle-label">{division.divisionName}</span>
                        <span className="pill-count">{division.codeCount}</span>
                      </button>

                      {divisionOpen ? (
                        <div className="tree-codes">
                          {codes ? (
                            codes.map((entry) => (
                              <SicCodeRow
                                key={entry.code}
                                entry={entry}
                                selected={selected.has(entry.code)}
                                onToggleSelect={onToggleSelect}
                                shortlistSource="tree"
                                acquisitionState={states[entry.code]}
                                onAcquire={onAcquire}
                              />
                            ))
                          ) : (
                            <div className="loading-panel">
                              <span className="spinner" />
                              Loading codes
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
